import { useState } from "react";
import { getWalletByPropFirm, useQuery } from "wasp/client/operations";
import { Pencil, Plus, Wallet } from "lucide-react";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";
import { WalletAddModal } from "../../../prop-firm-manager/WalletAddModal";
import { WalletInitialBalanceBanner } from "../../../prop-firm-manager/WalletInitialBalanceBanner";
import { WalletInitialBalanceModal } from "../../../prop-firm-manager/WalletInitialBalanceModal";
import { WalletTransfersTable } from "../../../prop-firm-manager/WalletTransfersTable";
import { WalletWithdrawalsTable } from "../../../prop-firm-manager/WalletWithdrawalsTable";
import { useLiteData } from "../../data/LiteDataProvider";
import { Card, Segmented } from "../../ui/ui";

interface WalletData {
  initialBalance: number;
  availableBalance: number;
  approvedTransfersSum: number;
  completedWithdrawalsSum: number;
  transfers: { id: string; date: string | Date; netAmount: number; status: string; account?: { name: string } | null }[];
  withdrawals: { id: string; date: string | Date; amount: number; status: string }[];
}

/** Portefeuille TPT : mêmes requêtes et même fenêtre d'ajout que le PFM Advanced. */
export function WalletCard({ wallet: ctx }: { wallet: NonNullable<ReturnType<typeof useLiteData>["tptWallet"]> }) {
  const t = useTranslation();
  const w = t.lite.pfm.wallet;
  const status = t.propFirmManager.wallet;
  const { fmt, refetchAll } = useLiteData();
  const [adding, setAdding] = useState(false);
  const [editInitial, setEditInitial] = useState(false);
  // « Récents » : aperçu ; « Transferts » / « Retraits » : tableaux de l'Advanced (statut, suppression).
  const [view, setView] = useState<"recent" | "transfers" | "withdrawals">("recent");
  const { data, refetch } = useQuery(getWalletByPropFirm, { propFirmTemplateId: ctx.propFirmTemplateId });
  const wallet = data as WalletData | null | undefined;
  const onMutated = () => { refetch(); refetchAll(); };

  const statusLabel: Record<string, string> = {
    pending: status.statusPending, approved: status.statusApproved, rejected: status.statusRejected, completed: status.statusCompleted,
  };
  const moves = [
    ...(wallet?.transfers ?? []).map(x => ({ id: x.id, date: new Date(x.date), label: w.transfer(x.account?.name ?? ""), amount: x.netAmount, status: x.status })),
    ...(wallet?.withdrawals ?? []).map(x => ({ id: x.id, date: new Date(x.date), label: w.withdrawal, amount: -x.amount, status: x.status })),
  ].sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 8);

  return (
    <Card title={status.title} subtitle={w.subtitle} action={<Wallet size={16} className="text-muted-foreground" />}>
      <div className="-mt-1 mb-3 empty:hidden"><WalletInitialBalanceBanner propFirmTemplateId={ctx.propFirmTemplateId} onMutated={onMutated} /></div>
      <div className="flex items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{w.available}</div>
          <div className="font-mono tabular-nums text-3xl font-semibold mt-1 truncate">{fmt.money(wallet?.availableBalance ?? 0, { sign: false, decimals: true })}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}><Plus className="h-4 w-4 mr-1" />{w.add}</Button>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs mb-3">
        <div>
          <div className="text-muted-foreground flex items-center gap-1">
            {w.initial}
            <button type="button" onClick={() => setEditInitial(true)} className="p-0.5 rounded hover:bg-muted hover:text-foreground" title={status.editInitialBalance} aria-label={status.editInitialBalance}><Pencil size={11} /></button>
          </div>
          <div className="font-mono tabular-nums mt-0.5">{fmt.money(wallet?.initialBalance ?? 0, { sign: false })}</div>
        </div>
        <div><div className="text-muted-foreground">{w.transfers}</div><div className="font-mono tabular-nums mt-0.5 text-pnlPositive">{fmt.money(wallet?.approvedTransfersSum ?? 0)}</div></div>
        <div><div className="text-muted-foreground">{w.withdrawn}</div><div className="font-mono tabular-nums mt-0.5">{fmt.money(-(wallet?.completedWithdrawalsSum ?? 0))}</div></div>
      </div>
      <div className="mb-3">
        <Segmented size="sm" value={view} onChange={setView} options={[
          { id: "recent", label: w.recent },
          { id: "transfers", label: status.transfersTableTitle },
          { id: "withdrawals", label: status.withdrawalsTableTitle },
        ]} />
      </div>
      {view === "transfers" ? (
        <div className="overflow-x-auto"><WalletTransfersTable transfers={(wallet as any)?.transfers ?? []} onMutated={onMutated} /></div>
      ) : view === "withdrawals" ? (
        <div className="overflow-x-auto"><WalletWithdrawalsTable withdrawals={(wallet as any)?.withdrawals ?? []} onMutated={onMutated} /></div>
      ) : moves.length ? (
        <div className="divide-y divide-border text-sm border-t border-border">
          {moves.map(m => (
            <div key={m.id} className="flex items-center justify-between gap-3 py-2.5">
              <span className="min-w-0 truncate">
                <span className="text-muted-foreground mr-3 font-mono tabular-nums text-xs">{fmt.dateShort(m.date)}</span>
                {m.label}
                <span className="ml-2 text-[11px] text-muted-foreground/70">{statusLabel[m.status] ?? m.status}</span>
              </span>
              <span className={`font-mono tabular-nums shrink-0 ${m.amount > 0 ? "text-pnlPositive" : ""}`}>{fmt.money(m.amount, { decimals: true })}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-muted-foreground border-t border-border pt-3">{w.none}</p>}
      <WalletAddModal
        open={adding}
        onClose={() => setAdding(false)}
        propFirmTemplateId={ctx.propFirmTemplateId}
        tptAccounts={ctx.accounts}
        onMutated={onMutated}
      />
      <WalletInitialBalanceModal
        open={editInitial}
        onClose={() => setEditInitial(false)}
        propFirmTemplateId={ctx.propFirmTemplateId}
        currentInitialBalance={(wallet as any)?.initialBalance}
        currentInitialBalanceDate={(wallet as any)?.initialBalanceDate}
        onMutated={onMutated}
      />
    </Card>
  );
}
